import { useMemo, useCallback } from 'react';
import { useQuery, InfiniteData, useInfiniteQuery } from 'react-query';
import produce from 'immer';

import { request, queryClient, useCurrentUserId, toastError } from '@/utils';

import { transAuthor } from '../utils';

const UserList = {
  reposters_list: {
    url: '/article/reposters',
    resKey: 'reposters',
  },
  trusters_list: {
    url: '/article/trusters',
    resKey: 'trusters',
  },
  distrusters_list: {
    url: '/article/distrusters',
    resKey: 'distrusters',
  },
  readers_list: {
    url: '/article/readers',
    resKey: 'readers',
  },
} as const;

export type UnionAccountWithRank = UnionAccount & {
  is_following: boolean;
  rank_at?: number;
  _author?: string;
};

const buildFeedOperationUserListHook =
  <R extends UnionAccountWithRank, T extends keyof typeof UserList>(type: T) =>
  (articleId: number) => {
    const { url, resKey } = UserList[type];

    const queryKey = useMemo(() => [url, articleId], [url, articleId]);
    const params = {
      limit: 100,
      start: 0,
      id: articleId,
    };

    const select = useCallback(
      (res: Record<typeof resKey, R[]>) => {
        return res[resKey];
      },
      [resKey],
    );

    return useQuery(
      queryKey,
      async () => {
        return request.get<Record<typeof resKey, R[]>>(url, params);
      },
      {
        enabled: !!articleId,
        select,
        cacheTime: 0,
        onError(err: any) {
          toastError(err);
        },
      },
    );
  };

export const useRepostersList = buildFeedOperationUserListHook<
  UnionAccountWithRank,
  'reposters_list'
>('reposters_list');
export const useArticleTrustersList = buildFeedOperationUserListHook<
  UnionAccountWithRank,
  'trusters_list'
>('trusters_list');
export const useArticleDistrustersList = buildFeedOperationUserListHook<
  UnionAccountWithRank,
  'distrusters_list'
>('distrusters_list');

export const useArticleReadersList = buildFeedOperationUserListHook<
  UnionAccountWithRank,
  'readers_list'
>('readers_list');

const InfiniteUserList = {
  pay_users: {
    url: '/article/pay_users',
    resKey: 'users',
  },
  pay_qa_watchers: {
    url: '/article/pay_qa/watcher_list',
    resKey: 'users',
  },
  quest_joiners: {
    url: '/quest/join_users',
    resKey: 'users',
  },
};

const buildInfiniteUsersHook =
  <T extends keyof typeof InfiniteUserList>(type: T) =>
  (id: number) => {
    const currentAddress = useCurrentUserId();
    const { url, resKey } = InfiniteUserList[type];

    const select = useCallback(
      (data: InfiniteData<{ total_count: number } & Record<string, UnionAccountWithRank[]>>) => {
        return produce(data, draft => {
          draft.pages.forEach(page =>
            page.users?.forEach(x => {
              x._author = transAuthor({ user: x });
            }),
          );
        });
      },
      [],
    );

    const {
      data,
      isLoading,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      isFetchingPreviousPage,
      fetchPreviousPage,
      refetch,
      isFetching,
    } = useInfiniteQuery(
      [url, currentAddress, id],
      async ({ pageParam = 0 }) => {
        const params = {
          id,
          start: pageParam,
          limit: 100,
        };

        return request.get<{ total_count: number } & Record<string, UnionAccountWithRank[]>>(
          url,
          params,
        );
      },
      {
        staleTime: 0,
        cacheTime: 0,
        keepPreviousData: false,
        getNextPageParam: (lastPage, pages) => {
          const total = pages.reduce((s, n) => s + n[resKey]?.length, 0);

          if (total >= lastPage?.total_count) {
            return;
          }
          return total;
        },
        select,
        onError(err: any) {
          toastError(err || 'Failed');
        },
      },
    );

    const result = useMemo(
      () => ({
        users: data?.pages.flatMap(x => x[resKey]),
        total_count: data?.pages?.[0].total_count || 0,
      }),
      [data, resKey],
    );

    return {
      result,
      isLoading,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      refetch,
      isFetching,
      isFetchingPreviousPage,
      fetchPreviousPage,
    };
  };

export const useArticlePayUserList = buildInfiniteUsersHook<'pay_users'>('pay_users');
export const useArticlePayQaWathcersList =
  buildInfiniteUsersHook<'pay_qa_watchers'>('pay_qa_watchers');
export const useArticleQuestJoiners = buildInfiniteUsersHook('quest_joiners');

export const changeArticleUsersFollowing = (
  articleId: number,
  userId: string,
  currentAddress?: string,
) => {
  Object.values(InfiniteUserList).forEach(({ url, resKey }) => {
    queryClient.setQueriesData<InfiniteData<Record<string, UnionAccountWithRank[]>>>(
      [url, currentAddress, articleId],
      pre => {
        if (!pre) {
          return pre!;
        }

        return produce(pre, pre => {
          if (pre) {
            pre.pages?.some(x => {
              return x[resKey].some(x => {
                if (x.id === userId) {
                  x.is_following = !x.is_following;

                  return true;
                }

                return false;
              });
            });
          }
        });
      },
    );
  });
};
